/**
 * QuotaOverview Component
 *
 * Dashboard showing quota usage overview for all providers and pools.
 * Displays expandable provider cards with pool-level details.
 */

import React, { useState, useMemo } from 'react';
import { StatusBadge } from '../shared/StatusBadge';
import type { 
  QuotaUsageResponseNew, 
  ModelHealthResponse, 
  QuotaUsageItemNew, 
  ModelHealthItem 
} from '../../../services/llmAdminApi';

export interface QuotaOverviewProps {
  quotaData: QuotaUsageResponseNew;
  healthData: ModelHealthResponse;
  onReset: (provider_name: string, pool_index: number, model_use?: string, quota_type?: string) => void;
}

interface ProviderSummary {
  provider_name: string;
  total_requests: number;
  total_tokens: number;
  pool_count: number;
  alert_count: number;
  pools: Array<{
    pool_index: number;
    model_count: number;
    quota_summary: {
      concurrency: number;
      token_per_minute: number;
      token_per_hour: number;
      token_per_day: number;
      request_per_minute: number;
      request_per_hour: number;
      request_per_day: number;
    };
    health_status: {
      available_count: number;
      unavailable_count: number;
    };
  }>;
}

const calculatePercentage = (used: number, limit: number | null): number | null => {
  if (limit === null || limit === 0) return null;
  return Math.min((used / limit) * 100, 100);
};

const getAlertLevel = (percentage: number | null): 'none' | 'warning' | 'alert' => {
  if (percentage === null) return 'none';
  if (percentage >= 100) return 'alert';
  if (percentage >= 80) return 'warning';
  return 'none';
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

export const QuotaOverview: React.FC<QuotaOverviewProps> = ({
  quotaData,
  healthData,
  onReset,
}) => {
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set());

  const providerSummaries = useMemo(() => {
    const summaries: Record<string, ProviderSummary> = {};

    quotaData.usage.forEach((item: QuotaUsageItemNew) => {
      const { provider_name } = item;

      if (!summaries[provider_name]) {
        summaries[provider_name] = {
          provider_name,
          total_requests: 0,
          total_tokens: 0,
          pool_count: 0,
          alert_count: 0,
          pools: [],
        };
      }

      const summary = summaries[provider_name];

      if (item.quota_type.startsWith('request_')) {
        summary.total_requests += item.used_count;
      } else if (item.quota_type.startsWith('token_')) {
        summary.total_tokens += item.used_count;
      }

      const percentage = calculatePercentage(item.used_count, item.limit_value);
      const alertLevel = getAlertLevel(percentage);
      if (alertLevel !== 'none') {
        summary.alert_count += 1;
      }
    });

    Object.keys(summaries).forEach((providerName) => {
      const providerPools = quotaData.usage.filter(
        (item) => item.provider_name === providerName
      );

      const uniquePools = new Set(providerPools.map((item) => item.pool_index));
      summaries[providerName].pool_count = uniquePools.size;

      uniquePools.forEach((poolIndex: number) => {
        const poolItems = providerPools.filter((item: QuotaUsageItemNew) => item.pool_index === poolIndex);
        const poolModels = healthData.models.filter(
          (m: ModelHealthItem) => m.provider_name === providerName && m.pool_index === poolIndex
        );

        const quotaSummary = {
          concurrency: 0,
          token_per_minute: 0,
          token_per_hour: 0,
          token_per_day: 0,
          request_per_minute: 0,
          request_per_hour: 0,
          request_per_day: 0,
        };

        poolItems.forEach((item: QuotaUsageItemNew) => {
          if (item.quota_type === 'concurrency') {
            quotaSummary.concurrency = item.used_count;
          } else if (item.quota_type === 'token_per_minute') {
            quotaSummary.token_per_minute = item.used_count;
          } else if (item.quota_type === 'token_per_hour') {
            quotaSummary.token_per_hour = item.used_count;
          } else if (item.quota_type === 'token_per_day') {
            quotaSummary.token_per_day = item.used_count;
          } else if (item.quota_type === 'request_per_minute') {
            quotaSummary.request_per_minute = item.used_count;
          } else if (item.quota_type === 'request_per_hour') {
            quotaSummary.request_per_hour = item.used_count;
          } else if (item.quota_type === 'request_per_day') {
            quotaSummary.request_per_day = item.used_count;
          }
        });

        const healthStatus = {
          available_count: poolModels.filter((m: ModelHealthItem) => m.available).length,
          unavailable_count: poolModels.filter((m: ModelHealthItem) => !m.available).length,
        };

        summaries[providerName].pools.push({
          pool_index: poolIndex,
          model_count: poolModels.length,
          quota_summary: quotaSummary,
          health_status: healthStatus,
        });
      });

      summaries[providerName].pools.sort((a, b) => a.pool_index - b.pool_index);
    });

    return Object.values(summaries).sort((a, b) => a.provider_name.localeCompare(b.provider_name));
  }, [quotaData, healthData]);

  const toggleProvider = (providerName: string) => {
    setExpandedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(providerName)) {
        next.delete(providerName);
      } else {
        next.add(providerName);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-gray-700">提供商概览</h4>

      {providerSummaries.length === 0 ? (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-sm">
          暂无配额数据
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {providerSummaries.map((provider) => (
            <div
              key={provider.provider_name}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              <div
                className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleProvider(provider.provider_name)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h5 className="text-sm font-medium text-gray-800">
                      {provider.provider_name}
                    </h5>
                    {provider.alert_count > 0 && (
                      <StatusBadge variant="error" size="sm" dot pulse>
                        {provider.alert_count} 个预警
                      </StatusBadge>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {expandedProviders.has(provider.provider_name) ? '收起' : '展开'}
                  </span>
                </div>

                <div className="flex items-center gap-4 text-xs text-gray-600">
                  <span>{provider.pool_count} 个池</span>
                  <span>{formatNumber(provider.total_requests)} 次请求</span>
                  <span>{formatNumber(provider.total_tokens)} 个 Token</span>
                </div>
              </div>

              {expandedProviders.has(provider.provider_name) && (
                <div className="p-4 bg-white border-t border-gray-200 space-y-4">
                  {provider.pools.map((pool) => (
                    <div key={pool.pool_index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h6 className="text-xs font-medium text-gray-700">
                          Pool #{pool.pool_index}
                        </h6>
                        <div className="flex items-center gap-2">
                          {pool.health_status.unavailable_count > 0 && (
                            <StatusBadge variant="error" size="sm" dot>
                              {pool.health_status.unavailable_count} 个不可用
                            </StatusBadge>
                          )}
                          {pool.health_status.available_count > 0 && (
                            <StatusBadge variant="success" size="sm" dot>
                              {pool.health_status.available_count} 个可用
                            </StatusBadge>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="space-y-1">
                          <div className="text-gray-600">并发数</div>
                          <div className="text-gray-800">{pool.quota_summary.concurrency}</div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-gray-600">请求/分钟</div>
                          <div className="text-gray-800">{formatNumber(pool.quota_summary.request_per_minute)}</div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-gray-600">请求/小时</div>
                          <div className="text-gray-800">{formatNumber(pool.quota_summary.request_per_hour)}</div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-gray-600">请求/天</div>
                          <div className="text-gray-800">{formatNumber(pool.quota_summary.request_per_day)}</div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => onReset(provider.provider_name, pool.pool_index)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        重置配额
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuotaOverview;